// src/features/bom/pages/BomPage.tsx
import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Pagination } from '@/components/ui/Pagination';
import {
    getAllRecipes, deactivateRecipe, BomRecipe,
} from '../services/bomApi';
import {
    Beaker, Plus, Search, X, Package, ChefHat, Layers, RefreshCw, Eye, Trash2,
    FlaskConical, Timer, DollarSign, CheckCircle2, XCircle, LayoutGrid, List, Pencil,
    Calculator, TrendingUp, Flame, Sparkles, ChevronDown, ChevronUp, Coins, Scale,
    AlertCircle, ArrowUpRight, Award, Zap, Utensils, Factory, ArrowRight,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import { extractApiError } from '@/lib/api-error';

type ViewMode = 'card' | 'table';

export function BomPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const isProduction = location.pathname.startsWith('/bom');
    const routePrefix = isProduction ? '/bom' : '/recipes';

    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
    const [selectedIngredient, setSelectedIngredient] = useState<string | null>(null);
    const [activeIntelligenceTab, setActiveIntelligenceTab] = useState<'economics' | 'ingredients'>('economics');
    const [isIntelligenceExpanded, setIsIntelligenceExpanded] = useState<boolean>(!isProduction);
    const [confirmDelete, setConfirmDelete] = useState<BomRecipe | null>(null);
    const [viewMode, setViewMode] = useState<ViewMode>('card');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(12);

    const { data: recipes, isLoading, isFetching, refetch } = useQuery({
        queryKey: ['bom-recipes', user?.tenantId],
        queryFn: () => getAllRecipes(user!.tenantId),
        enabled: !!user?.tenantId,
    });

    const deactivateMutation = useMutation({
        mutationFn: (recipeId: string) => deactivateRecipe(user!.tenantId, recipeId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['bom-recipes'] });
            addToast({ type: 'success', title: 'Recipe deactivated' });
            setConfirmDelete(null);
        },
        onError: (err: any) => {
            const { title, message } = extractApiError(err, 'Failed');
            addToast({ type: 'error', title, message, duration: 10000 });
        },
    });

    const recipesList = useMemo(() => (recipes || []) as BomRecipe[], [recipes]);
    const activeRecipes = useMemo(() => recipesList.filter(r => r.isActive), [recipesList]);
    const inactiveRecipes = useMemo(() => recipesList.filter(r => !r.isActive), [recipesList]);

    // Reset pagination on filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [search, statusFilter, selectedIngredient]);

    // Formatters
    const formatNaira = (val: number) => {
        const num = Number(val) || 0;
        return `₦${num.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const getIngredientName = (item: any): string => {
        return item.rawMaterialName || item.rawMaterialSku || 'Unknown';
    };

    const getSellingPriceInfo = (recipe: BomRecipe): { price: number; isSuggested: boolean } => {
        const rawPrice = recipe.finishedProductRetailPrice ? parseFloat(recipe.finishedProductRetailPrice) : 0;
        if (rawPrice > 0) return { price: rawPrice, isSuggested: false };
        if (recipe.costPerYield) return { price: recipe.costPerYield, isSuggested: true };
        return { price: 0, isSuggested: false };
    };

    const getProfitMargin = (recipe: BomRecipe): number | null => {
        const { price, isSuggested } = getSellingPriceInfo(recipe);
        if (isSuggested) return null;
        if (recipe.costPerYield && price > 0) {
            return ((price - recipe.costPerYield) / price) * 100;
        }
        return null;
    };

    const getFoodCostPct = (recipe: BomRecipe): number | null => {
        const { price } = getSellingPriceInfo(recipe);
        if (price > 0 && recipe.costPerYield) {
            return (recipe.costPerYield / price) * 100;
        }
        return null;
    };

    // Food emoji mapper
    const getRecipeEmoji = (name: string): string => {
        const text = (name || '').toLowerCase();
        if (text.includes('jollof') || text.includes('fried rice') || text.includes('ofada') || text.includes('rice')) return '🍚';
        if (text.includes('chicken') || text.includes('poultry') || text.includes('wings') || text.includes('drumstick')) return '🍗';
        if (text.includes('beef') || text.includes('steak') || text.includes('suya') || text.includes('meat') || text.includes('goat') || text.includes('asun')) return '🥩';
        if (text.includes('fish') || text.includes('tilapia') || text.includes('salmon') || text.includes('catfish') || text.includes('seafood')) return '🐟';
        if (text.includes('soup') || text.includes('stew') || text.includes('egusi') || text.includes('ogbono') || text.includes('pepper soup')) return '🍲';
        if (text.includes('burger') || text.includes('sandwich')) return '🍔';
        if (text.includes('pizza')) return '🍕';
        if (text.includes('shawarma') || text.includes('wrap') || text.includes('roll')) return '🌯';
        if (text.includes('salad') || text.includes('veggie')) return '🥗';
        if (text.includes('pasta') || text.includes('spaghetti') || text.includes('noodles')) return '🍝';
        if (text.includes('bread') || text.includes('toast') || text.includes('pastry') || text.includes('croissant')) return '🥐';
        if (text.includes('cake') || text.includes('donut') || text.includes('dessert')) return '🍰';
        if (text.includes('plantain') || text.includes('dodo') || text.includes('fries') || text.includes('chips')) return '🍟';
        if (text.includes('yam') || text.includes('potato')) return '🥔';
        if (text.includes('drink') || text.includes('juice') || text.includes('smoothie')) return '🧃';
        return '🍽️';
    };

    // Owner Economics ("What it can make for the owner")
    const ownerEconomics = useMemo(() => {
        let totalPotentialBatchRevenue = 0;
        let totalPotentialBatchCost = 0;
        let totalPortions = 0;

        const dishEconomics: Array<{
            recipe: BomRecipe;
            sellingPrice: number;
            costPerServing: number;
            profitPerServing: number;
            marginPct: number;
            foodCostPct: number;
            yieldQty: number;
            batchRevenue: number;
            batchCost: number;
            batchProfit: number;
        }> = [];

        activeRecipes.forEach(r => {
            const yieldQty = parseFloat(r.yieldQuantity) || 1;
            const retailPrice = r.finishedProductRetailPrice ? parseFloat(r.finishedProductRetailPrice) : 0;
            const costPerServing = r.costPerYield || (r.totalCost ? r.totalCost / yieldQty : 0);
            const sellingPrice = retailPrice > 0 ? retailPrice : (costPerServing > 0 ? costPerServing * 1.5 : 0);

            const batchCost = r.totalCost || (costPerServing * yieldQty);
            const batchRevenue = sellingPrice * yieldQty;
            const batchProfit = batchRevenue - batchCost;
            const profitPerServing = sellingPrice - costPerServing;
            const marginPct = sellingPrice > 0 ? (profitPerServing / sellingPrice) * 100 : 0;
            const foodCostPct = sellingPrice > 0 ? (costPerServing / sellingPrice) * 100 : 0;

            totalPotentialBatchRevenue += batchRevenue;
            totalPotentialBatchCost += batchCost;
            totalPortions += yieldQty;

            dishEconomics.push({
                recipe: r,
                sellingPrice,
                costPerServing,
                profitPerServing,
                marginPct,
                foodCostPct,
                yieldQty,
                batchRevenue,
                batchCost,
                batchProfit,
            });
        });

        const totalPotentialBatchProfit = totalPotentialBatchRevenue - totalPotentialBatchCost;
        const overallMarginPct = totalPotentialBatchRevenue > 0
            ? (totalPotentialBatchProfit / totalPotentialBatchRevenue) * 100
            : 0;
        const overallFoodCostPct = totalPotentialBatchRevenue > 0
            ? (totalPotentialBatchCost / totalPotentialBatchRevenue) * 100
            : 0;

        // Top profit-generating dishes per serving
        const topProfitDishes = [...dishEconomics]
            .filter(d => d.profitPerServing > 0)
            .sort((a, b) => b.profitPerServing - a.profitPerServing)
            .slice(0, 4);

        return {
            totalPotentialBatchRevenue,
            totalPotentialBatchCost,
            totalPotentialBatchProfit,
            overallMarginPct,
            overallFoodCostPct,
            totalPortions,
            dishEconomics,
            topProfitDishes,
        };
    }, [activeRecipes]);

    // Ingredients Used & Kitchen Demand Intelligence
    const ingredientsIntelligence = useMemo(() => {
        const ingredientMap: Record<string, {
            id: string;
            name: string;
            sku?: string;
            recipeCount: number;
            recipeNames: string[];
            totalCostContribution: number;
            avgWasteFactor: number;
            totalQtyRecorded: number;
            uomNames: Set<string>;
        }> = {};

        let totalWasteSum = 0;
        let totalItemsCount = 0;

        recipesList.forEach(recipe => {
            recipe.items.forEach(item => {
                const name = item.rawMaterialName || item.rawMaterialSku || 'Unknown Ingredient';
                const key = (item.rawMaterialId || name).trim().toLowerCase();
                const waste = parseFloat(item.wasteFactor) || 0;
                const cost = item.cost || 0;
                const qty = parseFloat(item.quantity) || 0;

                totalWasteSum += waste;
                totalItemsCount += 1;

                if (!ingredientMap[key]) {
                    ingredientMap[key] = {
                        id: item.rawMaterialId,
                        name: item.rawMaterialName || item.rawMaterialSku || 'Unknown',
                        sku: item.rawMaterialSku,
                        recipeCount: 0,
                        recipeNames: [],
                        totalCostContribution: 0,
                        avgWasteFactor: waste,
                        totalQtyRecorded: 0,
                        uomNames: new Set(),
                    };
                }

                ingredientMap[key].recipeCount += 1;
                if (!ingredientMap[key].recipeNames.includes(recipe.name)) {
                    ingredientMap[key].recipeNames.push(recipe.name);
                }
                ingredientMap[key].totalCostContribution += cost;
                ingredientMap[key].totalQtyRecorded += qty;
                if (item.uomName || item.uomAbbreviation) {
                    ingredientMap[key].uomNames.add(item.uomName || item.uomAbbreviation || '');
                }
            });
        });

        const ingredientsList = Object.values(ingredientMap);

        // Sort by frequency of use across menu recipes
        const mostUsedIngredients = [...ingredientsList]
            .sort((a, b) => b.recipeCount - a.recipeCount);

        // Top cost drivers
        const topCostDrivers = [...ingredientsList]
            .filter(i => i.totalCostContribution > 0)
            .sort((a, b) => b.totalCostContribution - a.totalCostContribution)
            .slice(0, 5);

        const avgWasteFactor = totalItemsCount > 0 ? totalWasteSum / totalItemsCount : 0;

        return {
            uniqueCount: ingredientsList.length,
            totalUsages: totalItemsCount,
            mostUsedIngredients,
            topCostDrivers,
            avgWasteFactor,
        };
    }, [recipesList]);

    // Filter recipes
    const filteredRecipes = useMemo(() => {
        return recipesList.filter(r => {
            if (statusFilter === 'active' && !r.isActive) return false;
            if (statusFilter === 'inactive' && r.isActive) return false;

            if (selectedIngredient) {
                const target = selectedIngredient.toLowerCase().trim();
                const matchesIngredient = r.items.some(item => {
                    const name = (item.rawMaterialName || item.rawMaterialSku || '').toLowerCase().trim();
                    return name === target || item.rawMaterialId === selectedIngredient;
                });
                if (!matchesIngredient) return false;
            }

            if (search) {
                const q = search.toLowerCase();
                return (
                    r.name.toLowerCase().includes(q) ||
                    (r.finishedProductName || '').toLowerCase().includes(q) ||
                    (r.description || '').toLowerCase().includes(q) ||
                    r.items.some(item =>
                        (item.rawMaterialName || '').toLowerCase().includes(q) ||
                        (item.rawMaterialSku || '').toLowerCase().includes(q)
                    )
                );
            }
            return true;
        });
    }, [recipesList, search, statusFilter, selectedIngredient]);

    // Pagination
    const totalItems = filteredRecipes.length;
    const totalPages = Math.ceil(totalItems / pageSize);
    const paginatedRecipes = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredRecipes.slice(start, start + pageSize);
    }, [filteredRecipes, currentPage, pageSize]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50/20 font-sans pb-16">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-xs">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div className="flex items-center gap-4">
                            <div className={clsx(
                                'p-2.5 rounded-2xl shadow-md text-white',
                                isProduction
                                    ? 'bg-gradient-to-br from-indigo-600 to-teal-600 shadow-indigo-500/20'
                                    : 'bg-gradient-to-br from-amber-500 to-orange-600 shadow-amber-500/20'
                            )}>
                                {isProduction ? <Factory className="w-6 h-6" /> : <ChefHat className="w-6 h-6" />}
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">
                                        {isProduction ? 'BOM Manufacturing & Production Hub' : 'Recipe Catalog & Menu Economics'}
                                    </h1>
                                    <span className={clsx(
                                        'px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border',
                                        isProduction
                                            ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                                            : 'bg-amber-50 text-amber-700 border-amber-200'
                                    )}>
                                        {isProduction ? 'Manufacturing Operations' : 'Catalog & Economics'}
                                    </span>
                                </div>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    {isProduction
                                        ? `${recipesList.length} total formulas · ${activeRecipes.length} production-ready · ${ingredientsIntelligence.uniqueCount} required ingredients`
                                        : `${recipesList.length} total recipes · ${activeRecipes.length} active dishes · ${ingredientsIntelligence.uniqueCount} raw ingredients`}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            {isProduction ? (
                                <>
                                    <button
                                        onClick={() => navigate('/bom/production')}
                                        className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl hover:opacity-95 text-xs font-bold transition-all shadow-md shadow-amber-500/20"
                                    >
                                        <Flame className="w-4 h-4" /> Start Daily Batch
                                    </button>
                                    <button
                                        onClick={() => navigate('/bom/production-floors')}
                                        className="flex items-center gap-2 px-4 py-2.5 bg-indigo-50 text-indigo-800 border border-indigo-200 rounded-xl hover:bg-indigo-100 text-xs font-bold transition-all shadow-xs"
                                    >
                                        <Factory className="w-4 h-4 text-indigo-600" /> Production Floors
                                    </button>
                                    <button
                                        onClick={() => navigate('/recipes')}
                                        className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 text-xs font-bold transition-all"
                                        title="View Menu Economics Dashboard"
                                    >
                                        <Coins className="w-4 h-4 text-amber-600" /> Economics View
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        onClick={() => navigate('/bom')}
                                        className="flex items-center gap-2 px-4 py-2.5 bg-indigo-50 text-indigo-800 border border-indigo-200 rounded-xl hover:bg-indigo-100 text-xs font-bold transition-all shadow-xs"
                                        title="Switch to Production & Manufacturing Hub"
                                    >
                                        <Factory className="w-4 h-4 text-indigo-600" /> Manufacturing Hub
                                    </button>
                                    <button
                                        onClick={() => navigate('/bom-pos')}
                                        className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 text-xs font-bold transition-all"
                                    >
                                        <Utensils className="w-4 h-4 text-slate-500" /> Restaurant POS
                                    </button>
                                </>
                            )}
                            <button onClick={() => refetch()} disabled={isFetching}
                                className="flex items-center gap-1.5 px-3 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 text-xs font-semibold text-slate-600 transition-all">
                                <RefreshCw className={clsx('w-3.5 h-3.5', isFetching && 'animate-spin')} /> Refresh
                            </button>
                            <button onClick={() => navigate(`${routePrefix}/new`)}
                                className={clsx(
                                    'flex items-center gap-2 px-5 py-2.5 text-white rounded-xl hover:opacity-95 font-bold shadow-md text-xs',
                                    isProduction
                                        ? 'bg-gradient-to-r from-indigo-600 to-teal-600 shadow-indigo-500/20'
                                        : 'bg-gradient-to-r from-amber-500 to-orange-600 shadow-amber-500/20'
                                )}>
                                <Plus className="w-4 h-4" /> {isProduction ? 'New BOM Formula' : 'New Recipe'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-6">
                {/* PRODUCTION COMMAND STRIP (When on /bom) */}
                {isProduction && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div
                            onClick={() => navigate('/bom/production')}
                            className="p-5 bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-transparent border border-amber-200 rounded-3xl cursor-pointer hover:border-amber-400 hover:shadow-lg hover:shadow-amber-500/10 transition-all group"
                        >
                            <div className="flex items-center justify-between mb-3">
                                <div className="p-2.5 rounded-2xl bg-amber-500 text-white shadow-md shadow-amber-500/20">
                                    <Flame className="w-5 h-5" />
                                </div>
                                <ArrowRight className="w-4 h-4 text-amber-500 group-hover:translate-x-1 transition-transform" />
                            </div>
                            <h3 className="font-extrabold text-slate-900 text-sm">Batch Production</h3>
                            <p className="text-xs text-slate-500 mt-1">Cook today's batches, add portions, & log station waste</p>
                        </div>

                        <div
                            onClick={() => navigate('/bom/production-floors')}
                            className="p-5 bg-gradient-to-br from-indigo-500/10 via-blue-500/5 to-transparent border border-indigo-200 rounded-3xl cursor-pointer hover:border-indigo-400 hover:shadow-lg hover:shadow-indigo-500/10 transition-all group"
                        >
                            <div className="flex items-center justify-between mb-3">
                                <div className="p-2.5 rounded-2xl bg-indigo-600 text-white shadow-md shadow-indigo-500/20">
                                    <Factory className="w-5 h-5" />
                                </div>
                                <ArrowRight className="w-4 h-4 text-indigo-600 group-hover:translate-x-1 transition-transform" />
                            </div>
                            <h3 className="font-extrabold text-slate-900 text-sm">Production Floors</h3>
                            <p className="text-xs text-slate-500 mt-1">Staging locations, kitchen floors, & raw ingredient transfers</p>
                        </div>

                        <div
                            onClick={() => navigate('/kitchen')}
                            className="p-5 bg-gradient-to-br from-rose-500/10 via-pink-500/5 to-transparent border border-rose-200 rounded-3xl cursor-pointer hover:border-rose-400 hover:shadow-lg hover:shadow-rose-500/10 transition-all group"
                        >
                            <div className="flex items-center justify-between mb-3">
                                <div className="p-2.5 rounded-2xl bg-rose-600 text-white shadow-md shadow-rose-500/20">
                                    <ChefHat className="w-5 h-5" />
                                </div>
                                <ArrowRight className="w-4 h-4 text-rose-600 group-hover:translate-x-1 transition-transform" />
                            </div>
                            <h3 className="font-extrabold text-slate-900 text-sm">Kitchen Display (KDS)</h3>
                            <p className="text-xs text-slate-500 mt-1">Real-time station order queue & ticket completion</p>
                        </div>

                        <div
                            onClick={() => navigate('/bom-pos')}
                            className="p-5 bg-gradient-to-br from-emerald-500/10 via-teal-500/5 to-transparent border border-emerald-200 rounded-3xl cursor-pointer hover:border-emerald-400 hover:shadow-lg hover:shadow-emerald-500/10 transition-all group"
                        >
                            <div className="flex items-center justify-between mb-3">
                                <div className="p-2.5 rounded-2xl bg-emerald-600 text-white shadow-md shadow-emerald-500/20">
                                    <Utensils className="w-5 h-5" />
                                </div>
                                <ArrowRight className="w-4 h-4 text-emerald-600 group-hover:translate-x-1 transition-transform" />
                            </div>
                            <h3 className="font-extrabold text-slate-900 text-sm">Restaurant POS</h3>
                            <p className="text-xs text-slate-500 mt-1">Menu ordering with automatic BOM portion depletion</p>
                        </div>
                    </div>
                )}
                {/* 5 High-Impact KPI Overview Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3.5">
                    <QuickStat
                        label="Potential Menu Value"
                        value={formatNaira(ownerEconomics.totalPotentialBatchRevenue)}
                        subtitle={`1 batch of all ${activeRecipes.length} active dishes`}
                        icon={Coins}
                        gradient="from-emerald-500 to-teal-600"
                    />
                    <QuickStat
                        label="Projected Gross Profit"
                        value={formatNaira(ownerEconomics.totalPotentialBatchProfit)}
                        subtitle={`${ownerEconomics.overallMarginPct.toFixed(1)}% menu gross margin`}
                        icon={TrendingUp}
                        gradient="from-amber-500 to-orange-600"
                    />
                    <QuickStat
                        label="Total Ingredient Cost"
                        value={formatNaira(ownerEconomics.totalPotentialBatchCost)}
                        subtitle={`${ownerEconomics.overallFoodCostPct.toFixed(1)}% food cost benchmark`}
                        icon={Calculator}
                        gradient="from-rose-500 to-rose-600"
                    />
                    <QuickStat
                        label="Unique Ingredients"
                        value={`${ingredientsIntelligence.uniqueCount} items`}
                        subtitle={`${ingredientsIntelligence.totalUsages} recipe requirements`}
                        icon={Beaker}
                        gradient="from-indigo-500 to-purple-600"
                    />
                    <QuickStat
                        label="Batch Portions Capacity"
                        value={`${Math.round(ownerEconomics.totalPortions).toLocaleString()} servings`}
                        subtitle={`${activeRecipes.length} ready to cook in kitchen`}
                        icon={ChefHat}
                        gradient="from-slate-700 to-slate-900"
                    />
                </div>

                {/* Collapsible Owner Intelligence & Kitchen Supply Hub */}
                <div className="bg-white/95 rounded-3xl border border-amber-200/90 shadow-md shadow-amber-500/5 overflow-hidden backdrop-blur-sm">
                    {/* Panel Header */}
                    <div className="p-4 sm:p-5 bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-transparent border-b border-amber-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 rounded-2xl bg-amber-500 text-white shadow-sm shadow-amber-500/30">
                                <Sparkles className="w-5 h-5" />
                            </div>
                            <div>
                                <h2 className="text-base font-extrabold text-slate-900 flex items-center gap-2">
                                    Owner Economics & Kitchen Supply Intelligence
                                    <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-800 border border-amber-300">
                                        Live Analysis
                                    </span>
                                </h2>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    Real-time profitability forecasts, food cost benchmarks, and ingredient utilization breakdown
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 self-start md:self-auto">
                            {/* Tab Switcher */}
                            <div className="flex items-center gap-1 bg-amber-500/15 p-1 rounded-xl border border-amber-200">
                                <button
                                    onClick={() => setActiveIntelligenceTab('economics')}
                                    className={clsx(
                                        'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all',
                                        activeIntelligenceTab === 'economics'
                                            ? 'bg-white text-slate-900 shadow-xs'
                                            : 'text-amber-900 hover:text-amber-950'
                                    )}
                                >
                                    <DollarSign className="w-3.5 h-3.5 text-emerald-600" /> Menu Economics
                                </button>
                                <button
                                    onClick={() => setActiveIntelligenceTab('ingredients')}
                                    className={clsx(
                                        'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all',
                                        activeIntelligenceTab === 'ingredients'
                                            ? 'bg-white text-slate-900 shadow-xs'
                                            : 'text-amber-900 hover:text-amber-950'
                                    )}
                                >
                                    <Beaker className="w-3.5 h-3.5 text-indigo-600" /> Ingredients Used ({ingredientsIntelligence.uniqueCount})
                                </button>
                            </div>

                            <button
                                onClick={() => setIsIntelligenceExpanded(!isIntelligenceExpanded)}
                                className="p-2 hover:bg-slate-100 rounded-xl text-slate-500 hover:text-slate-800 transition-colors"
                                title={isIntelligenceExpanded ? 'Collapse section' : 'Expand section'}
                            >
                                {isIntelligenceExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                            </button>
                        </div>
                    </div>

                    {/* Panel Body */}
                    <AnimatePresence>
                        {isIntelligenceExpanded && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.2 }}
                                className="p-5 sm:p-6"
                            >
                                {activeIntelligenceTab === 'economics' ? (
                                    /* Tab 1: Menu Economics & Profitability */
                                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                                        {/* What it can make for the owner summary */}
                                        <div className="lg:col-span-5 flex flex-col justify-between space-y-4 bg-slate-50/70 p-5 rounded-2xl border border-slate-200/80">
                                            <div>
                                                <div className="flex items-center justify-between mb-3">
                                                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                                                        What It Makes For The Owner
                                                    </span>
                                                    <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-100 px-2.5 py-0.5 rounded-full">
                                                        1 Batch Capacity
                                                    </span>
                                                </div>

                                                <p className="text-xs text-slate-600 mb-4">
                                                    If you prepare and sell 1 full batch of every active recipe in your catalog, here is your financial return:
                                                </p>

                                                <div className="space-y-2.5 text-xs">
                                                    <div className="flex items-center justify-between p-2.5 bg-white rounded-xl border border-slate-100">
                                                        <span className="text-slate-600 font-medium">Potential Gross Sales</span>
                                                        <span className="font-extrabold text-slate-900 text-sm">
                                                            {formatNaira(ownerEconomics.totalPotentialBatchRevenue)}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center justify-between p-2.5 bg-white rounded-xl border border-slate-100">
                                                        <span className="text-slate-600 font-medium">Raw Ingredients Expense (COGS)</span>
                                                        <span className="font-extrabold text-rose-600 text-sm">
                                                            - {formatNaira(ownerEconomics.totalPotentialBatchCost)}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center justify-between p-3 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-xl border border-emerald-200">
                                                        <div className="flex items-center gap-1.5 font-bold text-emerald-900">
                                                            <Award className="w-4 h-4 text-emerald-600" />
                                                            <span>Estimated Take-Home Profit</span>
                                                        </div>
                                                        <span className="font-black text-emerald-700 text-base">
                                                            {formatNaira(ownerEconomics.totalPotentialBatchProfit)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Food Cost Ratio Benchmark Meter */}
                                            <div className="pt-3 border-t border-slate-200/80">
                                                <div className="flex items-center justify-between text-xs mb-1.5">
                                                    <span className="font-bold text-slate-700 flex items-center gap-1.5">
                                                        <Scale className="w-3.5 h-3.5 text-slate-500" /> Food Cost Benchmark Ratio
                                                    </span>
                                                    <span className={clsx(
                                                        'font-extrabold text-xs px-2 py-0.5 rounded-full border',
                                                        ownerEconomics.overallFoodCostPct <= 32
                                                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                                            : ownerEconomics.overallFoodCostPct <= 45
                                                            ? 'bg-amber-50 text-amber-700 border-amber-200'
                                                            : 'bg-rose-50 text-rose-700 border-rose-200'
                                                    )}>
                                                        {ownerEconomics.overallFoodCostPct.toFixed(1)}% {ownerEconomics.overallFoodCostPct <= 32 ? '· Prime Profit' : ownerEconomics.overallFoodCostPct <= 45 ? '· Standard' : '· High Cost'}
                                                    </span>
                                                </div>

                                                <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden mb-1.5">
                                                    <div
                                                        className={clsx(
                                                            'h-2 rounded-full transition-all duration-500',
                                                            ownerEconomics.overallFoodCostPct <= 32
                                                                ? 'bg-emerald-500'
                                                                : ownerEconomics.overallFoodCostPct <= 45
                                                                ? 'bg-amber-500'
                                                                : 'bg-rose-500'
                                                        )}
                                                        style={{ width: `${Math.min(ownerEconomics.overallFoodCostPct, 100)}%` }}
                                                    />
                                                </div>
                                                <p className="text-[10px] text-slate-400">
                                                    Standard restaurant food costs target 28%–35%. Keeping ingredients under 35% yields high cash flow.
                                                </p>
                                            </div>
                                        </div>

                                        {/* Top Profit Stars Column */}
                                        <div className="lg:col-span-7 flex flex-col justify-between">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center gap-2">
                                                    <Zap className="w-4 h-4 text-amber-500" />
                                                    <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                                                        Top Profit Star Dishes (Highest Net Per Serving)
                                                    </span>
                                                </div>
                                                <span className="text-[11px] text-slate-400">
                                                    Ranked by profit pocketed per portion
                                                </span>
                                            </div>

                                            {ownerEconomics.topProfitDishes.length === 0 ? (
                                                <div className="p-8 text-center text-xs text-slate-400 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                                                    No dishes with positive margin yet. Set retail prices and ingredient costs to see profit rankings.
                                                </div>
                                            ) : (
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                    {ownerEconomics.topProfitDishes.map((dish, idx) => (
                                                        <div
                                                            key={dish.recipe.id}
                                                            onClick={() => navigate(`/bom/${dish.recipe.id}`)}
                                                            className="group p-3.5 bg-white rounded-2xl border border-slate-200 hover:border-amber-400 hover:shadow-md transition-all cursor-pointer flex flex-col justify-between"
                                                        >
                                                            <div>
                                                                <div className="flex items-start justify-between gap-2 mb-2">
                                                                    <div className="flex items-center gap-2.5 min-w-0">
                                                                        <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center text-lg shrink-0">
                                                                            {getRecipeEmoji(dish.recipe.name)}
                                                                        </div>
                                                                        <div className="min-w-0">
                                                                            <h4 className="font-bold text-xs text-slate-900 truncate group-hover:text-amber-700 transition-colors">
                                                                                {dish.recipe.name}
                                                                            </h4>
                                                                            <p className="text-[10px] text-slate-400 truncate">
                                                                                Yield: {dish.yieldQty} {dish.recipe.yieldUomName || 'servings'}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                    <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full font-bold text-[10px] whitespace-nowrap">
                                                                        {dish.marginPct.toFixed(0)}% Margin
                                                                    </span>
                                                                </div>

                                                                <div className="grid grid-cols-2 gap-2 p-2 bg-slate-50/80 rounded-xl text-[11px] mb-2.5">
                                                                    <div>
                                                                        <p className="text-[9px] text-slate-400 uppercase font-semibold">Menu Price</p>
                                                                        <p className="font-bold text-slate-800">{formatNaira(dish.sellingPrice)}</p>
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-[9px] text-slate-400 uppercase font-semibold">Cost / Serving</p>
                                                                        <p className="font-bold text-purple-700">{formatNaira(dish.costPerServing)}</p>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div className="flex items-center justify-between pt-2 border-t border-slate-100 text-xs">
                                                                <div>
                                                                    <span className="text-[10px] text-slate-400">Profit / Plate: </span>
                                                                    <span className="font-black text-emerald-600">
                                                                        +{formatNaira(dish.profitPerServing)}
                                                                    </span>
                                                                </div>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        navigate('/bom/production');
                                                                    }}
                                                                    className="px-2.5 py-1 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-[10px] font-bold shadow-xs transition-colors"
                                                                >
                                                                    Cook
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    /* Tab 2: Ingredients Used & Kitchen Demand */
                                    <div className="space-y-5">
                                        <div>
                                            <div className="flex items-center justify-between mb-2">
                                                <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                                                    <Beaker className="w-3.5 h-3.5 text-indigo-500" />
                                                    Kitchen Raw Materials Used (Click any tag to filter recipes below)
                                                </h3>
                                                {selectedIngredient && (
                                                    <button
                                                        onClick={() => setSelectedIngredient(null)}
                                                        className="text-xs font-bold text-amber-700 hover:text-amber-900 flex items-center gap-1"
                                                    >
                                                        <X className="w-3 h-3" /> Clear Selection
                                                    </button>
                                                )}
                                            </div>
                                            <p className="text-xs text-slate-500 mb-3">
                                                Showing frequency across your recipe database. Click an ingredient to instantly see which dishes depend on it:
                                            </p>

                                            {/* Interactive Ingredient Chips */}
                                            <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto pr-1">
                                                {ingredientsIntelligence.mostUsedIngredients.map((item) => {
                                                    const isSelected = selectedIngredient?.toLowerCase() === item.name.toLowerCase();
                                                    return (
                                                        <button
                                                            key={item.name}
                                                            onClick={() => setSelectedIngredient(isSelected ? null : item.name)}
                                                            className={clsx(
                                                                'px-3 py-1.5 rounded-xl text-xs font-semibold flex items-center gap-2 transition-all',
                                                                isSelected
                                                                    ? 'bg-amber-500 text-white shadow-md shadow-amber-500/25 ring-2 ring-amber-300 font-bold scale-105'
                                                                    : 'bg-slate-100 hover:bg-amber-100 hover:text-amber-900 text-slate-700'
                                                            )}
                                                        >
                                                            <span>{item.name}</span>
                                                            <span className={clsx(
                                                                'px-1.5 py-0.2 rounded-md text-[10px] font-extrabold',
                                                                isSelected ? 'bg-amber-600 text-white' : 'bg-white text-slate-600 border border-slate-200'
                                                            )}>
                                                                {item.recipeCount} {item.recipeCount === 1 ? 'recipe' : 'recipes'}
                                                            </span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Bottom Grid: Cost Drivers & Shrinkage */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-200/80">
                                            {/* Top Cost Driver Ingredients */}
                                            <div className="p-4 bg-slate-50/70 rounded-2xl border border-slate-200/80">
                                                <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                                    <DollarSign className="w-3.5 h-3.5 text-rose-500" />
                                                    High Cost-Driver Ingredients
                                                </h4>
                                                <p className="text-[11px] text-slate-500 mb-3">
                                                    Raw materials with the largest monetary footprint across all recipes:
                                                </p>
                                                <div className="space-y-2">
                                                    {ingredientsIntelligence.topCostDrivers.map((ing, i) => (
                                                        <div key={ing.name} className="flex items-center justify-between text-xs p-2 bg-white rounded-xl border border-slate-100">
                                                            <div className="flex items-center gap-2">
                                                                <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-bold text-[10px]">
                                                                    {i + 1}
                                                                </span>
                                                                <span className="font-semibold text-slate-800">{ing.name}</span>
                                                            </div>
                                                            <span className="font-bold text-slate-900">
                                                                {formatNaira(ing.totalCostContribution)}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Kitchen Waste & Shrinkage Profile */}
                                            <div className="p-4 bg-slate-50/70 rounded-2xl border border-slate-200/80 flex flex-col justify-between">
                                                <div>
                                                    <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                                        <Scale className="w-3.5 h-3.5 text-amber-500" />
                                                        Kitchen Waste & Shrinkage Profile
                                                    </h4>
                                                    <p className="text-[11px] text-slate-500 mb-3">
                                                        Average trim, peeling, and cooking shrinkage configured across all recipe ingredients:
                                                    </p>
                                                    <div className="p-3 bg-white rounded-xl border border-slate-100 flex items-center justify-between">
                                                        <div>
                                                            <p className="text-[10px] text-slate-400 uppercase font-semibold">Average Trimming Waste</p>
                                                            <p className="text-xl font-black text-amber-600">
                                                                {ingredientsIntelligence.avgWasteFactor.toFixed(1)}%
                                                            </p>
                                                        </div>
                                                        <div className="p-2 bg-amber-50 rounded-xl text-amber-700">
                                                            <AlertCircle className="w-5 h-5" />
                                                        </div>
                                                    </div>
                                                </div>
                                                <p className="text-[10px] text-slate-400 mt-3">
                                                    Accurate waste factors prevent inventory discrepancies and protect your margins during batch cooking.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Active Ingredient Filter Banner */}
                {selectedIngredient && (
                    <motion.div
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center justify-between px-5 py-3 bg-amber-500/10 border border-amber-300 rounded-2xl text-xs text-amber-900 shadow-xs"
                    >
                        <div className="flex items-center gap-2.5">
                            <span className="font-semibold text-amber-800">Filtered by ingredient:</span>
                            <span className="px-3 py-1 bg-amber-500 text-white font-bold rounded-xl shadow-xs">
                                {selectedIngredient}
                            </span>
                            <span className="text-amber-700 font-medium">({filteredRecipes.length} recipes matched)</span>
                        </div>
                        <button
                            onClick={() => setSelectedIngredient(null)}
                            className="flex items-center gap-1.5 px-3 py-1 text-xs font-bold text-amber-800 hover:text-amber-950 hover:bg-amber-200/50 rounded-xl transition-colors"
                        >
                            <X className="w-3.5 h-3.5" /> Clear Filter
                        </button>
                    </motion.div>
                )}

                {/* Search & Filters */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-xs">
                    <div className="p-3 flex flex-col sm:flex-row gap-3">
                        <div className="relative flex-1">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search dishes, products, or raw ingredients..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full pl-11 pr-10 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 focus:bg-white transition-all outline-none"
                            />
                            {search && (
                                <button
                                    onClick={() => setSearch('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
                                {[{ key: 'all', label: 'All' }, { key: 'active', label: 'Active' }, { key: 'inactive', label: 'Inactive' }].map(f => (
                                    <button
                                        key={f.key}
                                        onClick={() => setStatusFilter(f.key as any)}
                                        className={clsx(
                                            'px-4 py-2 rounded-lg text-xs font-bold transition-all whitespace-nowrap',
                                            statusFilter === f.key ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-500 hover:text-slate-700'
                                        )}
                                    >
                                        {f.label}
                                    </button>
                                ))}
                            </div>
                            <div className="flex items-center bg-slate-100 rounded-xl p-1">
                                <button
                                    onClick={() => setViewMode('card')}
                                    className={clsx('p-2 rounded-lg text-xs font-medium transition-all', viewMode === 'card' ? 'bg-white text-amber-600 shadow-xs' : 'text-slate-500')}
                                    title="Card view"
                                >
                                    <LayoutGrid size={16} />
                                </button>
                                <button
                                    onClick={() => setViewMode('table')}
                                    className={clsx('p-2 rounded-lg text-xs font-medium transition-all', viewMode === 'table' ? 'bg-white text-amber-600 shadow-xs' : 'text-slate-500')}
                                    title="Table view"
                                >
                                    <List size={16} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content */}
                {isLoading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className="bg-white rounded-3xl border border-slate-200 p-5 animate-pulse">
                                <div className="h-4 w-28 bg-slate-200 rounded mb-3" />
                                <div className="h-3 w-20 bg-slate-100 rounded" />
                            </div>
                        ))}
                    </div>
                ) : filteredRecipes.length === 0 ? (
                    <EmptyState
                        icon={<FlaskConical className="w-20 h-20" />}
                        title={search || statusFilter !== 'all' || selectedIngredient ? "No matching recipes" : "No BOM recipes yet"}
                        description={search || statusFilter !== 'all' || selectedIngredient ? "Try adjusting your search, filters, or selected ingredient." : "Create your first Bill of Materials recipe."}
                        action={!search && statusFilter === 'all' && !selectedIngredient ? { label: isProduction ? 'Create First BOM Formula' : 'Create First Recipe', onClick: () => navigate(`${routePrefix}/new`) } : { label: 'Clear Filters', onClick: () => { setSearch(''); setStatusFilter('all'); setSelectedIngredient(null); } }}
                    />
                ) : viewMode === 'table' ? (
                    <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-xs">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-100 text-xs">
                                <thead>
                                    <tr className="bg-slate-50/80">
                                        <th className="px-5 py-3.5 text-left font-bold text-slate-500 uppercase">{isProduction ? 'Formula' : 'Recipe'}</th>
                                        <th className="px-5 py-3.5 text-left font-bold text-slate-500 uppercase">Linked Product</th>
                                        <th className="px-5 py-3.5 text-center font-bold text-slate-500 uppercase">Ingredients</th>
                                        <th className="px-5 py-3.5 text-center font-bold text-slate-500 uppercase">Yield</th>
                                        <th className="px-5 py-3.5 text-center font-bold text-slate-500 uppercase">Cost/Yield</th>
                                        <th className="px-5 py-3.5 text-center font-bold text-slate-500 uppercase">Price</th>
                                        <th className="px-5 py-3.5 text-center font-bold text-slate-500 uppercase">Food Cost %</th>
                                        <th className="px-5 py-3.5 text-center font-bold text-slate-500 uppercase">Margin</th>
                                        <th className="px-5 py-3.5 text-center font-bold text-slate-500 uppercase">Status</th>
                                        <th className="px-5 py-3.5 text-right font-bold text-slate-500 uppercase w-36">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {paginatedRecipes.map((recipe) => {
                                        const { price, isSuggested } = getSellingPriceInfo(recipe);
                                        const margin = getProfitMargin(recipe);
                                        const foodCostPct = getFoodCostPct(recipe);
                                        const emoji = getRecipeEmoji(recipe.name);

                                        return (
                                            <tr key={recipe.id} className="group hover:bg-amber-50/20 cursor-pointer transition-colors" onClick={() => navigate(`${routePrefix}/${recipe.id}`)}>
                                                <td className="px-5 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-2xl bg-amber-100 flex items-center justify-center text-xl shrink-0">
                                                            {emoji}
                                                        </div>
                                                        <div>
                                                            <p className="font-bold text-slate-900 truncate max-w-[200px]">{recipe.name}</p>
                                                            {recipe.description && <p className="text-[11px] text-slate-400 truncate max-w-[200px]">{recipe.description}</p>}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-5 py-4 text-slate-600 font-medium">{recipe.finishedProductName || '—'}</td>
                                                <td className="px-5 py-4 text-center">
                                                    <span className="px-2 py-1 bg-slate-100 text-slate-700 font-semibold rounded-lg">
                                                        {recipe.items.length} items
                                                    </span>
                                                </td>
                                                <td className="px-5 py-4 text-center text-slate-700 font-medium">{recipe.yieldQuantity} {recipe.yieldUomName || 'servings'}</td>
                                                <td className="px-5 py-4 text-center font-bold text-purple-700">{recipe.costPerYield ? formatNaira(recipe.costPerYield) : '—'}</td>
                                                <td className="px-5 py-4 text-center">
                                                    <span className="font-bold text-slate-800">{formatNaira(price)}</span>
                                                    {isSuggested && <span className="ml-1 text-[9px] bg-amber-100 text-amber-700 px-1 py-0.5 rounded font-bold">Auto</span>}
                                                </td>
                                                <td className="px-5 py-4 text-center">
                                                    {foodCostPct !== null ? (
                                                        <span className={clsx(
                                                            'px-2 py-0.5 rounded-full font-bold text-[10px]',
                                                            foodCostPct <= 32 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                                                            foodCostPct <= 45 ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                                                            'bg-rose-50 text-rose-700 border border-rose-200'
                                                        )}>
                                                            {foodCostPct.toFixed(0)}%
                                                        </span>
                                                    ) : '—'}
                                                </td>
                                                <td className="px-5 py-4 text-center">
                                                    {margin !== null ? (
                                                        <span className={clsx('font-bold', margin >= 30 ? 'text-emerald-600' : margin >= 15 ? 'text-amber-600' : 'text-rose-600')}>
                                                            {margin.toFixed(1)}%
                                                        </span>
                                                    ) : '—'}
                                                </td>
                                                <td className="px-5 py-4 text-center">
                                                    <Badge variant={recipe.isActive ? 'success' : 'danger'} size="sm">{recipe.isActive ? 'Active' : 'Inactive'}</Badge>
                                                </td>
                                                 <td className="px-5 py-4 text-right" onClick={e => e.stopPropagation()}>
                                                    <div className="flex items-center justify-end gap-1.5">
                                                        <button
                                                            onClick={() => navigate('/bom/production')}
                                                            className={clsx(
                                                                'px-2.5 py-1 text-white rounded-lg text-[10px] font-bold shadow-xs transition-colors flex items-center gap-1',
                                                                isProduction ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-amber-500 hover:bg-amber-600'
                                                            )}
                                                            title={isProduction ? "Run production batch" : "Cook batch in Daily Production"}
                                                        >
                                                            {isProduction ? <Zap size={11} /> : <ChefHat size={11} />}
                                                            <span>{isProduction ? 'Produce' : 'Cook'}</span>
                                                        </button>
                                                        <button onClick={() => navigate(`${routePrefix}/${recipe.id}/edit`)} className="p-1.5 rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50"><Pencil size={13} /></button>
                                                        {recipe.isActive && <button onClick={() => setConfirmDelete(recipe)} className="p-1.5 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-50"><Trash2 size={13} /></button>}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} pageSize={pageSize} onPageChange={setCurrentPage} onPageSizeChange={setPageSize} />
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {paginatedRecipes.map((recipe, idx) => {
                            const { price, isSuggested } = getSellingPriceInfo(recipe);
                            const margin = getProfitMargin(recipe);
                            const foodCostPct = getFoodCostPct(recipe);
                            const emoji = getRecipeEmoji(recipe.name);

                            return (
                                <motion.div
                                    key={recipe.id}
                                    initial={{ opacity: 0, y: 15 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: idx * 0.03 }}
                                    whileHover={{ y: -3 }}
                                    className={clsx(
                                        'group bg-white rounded-3xl border-2 transition-all duration-300 overflow-hidden flex flex-col justify-between shadow-xs hover:shadow-xl',
                                        recipe.isActive
                                            ? isProduction
                                                ? 'border-slate-200/90 hover:border-indigo-300 hover:shadow-indigo-500/10 cursor-pointer'
                                                : 'border-slate-200/90 hover:border-amber-300 hover:shadow-amber-500/10 cursor-pointer'
                                            : 'border-rose-200/80 bg-rose-50/20'
                                    )}
                                >
                                    <div className={clsx('h-1.5', recipe.isActive ? isProduction ? 'bg-gradient-to-r from-indigo-500 to-teal-500' : 'bg-gradient-to-r from-amber-400 to-orange-500' : 'bg-rose-300')} />
                                    <div className="p-5 flex-1 flex flex-col justify-between">
                                        <div>
                                            <div className="flex items-start justify-between mb-3" onClick={() => navigate(`${routePrefix}/${recipe.id}`)}>
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <div className={clsx(
                                                        'w-12 h-12 rounded-2xl flex items-center justify-center text-2xl shadow-xs shrink-0',
                                                        isProduction ? 'bg-indigo-50 text-indigo-700' : 'bg-amber-100'
                                                    )}>
                                                        {emoji}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <h3 className="font-extrabold text-slate-900 truncate tracking-tight">{recipe.name}</h3>
                                                        <p className="text-xs text-slate-400 truncate">{recipe.finishedProductName || 'Composite Recipe'}</p>
                                                    </div>
                                                </div>
                                                <Badge variant={recipe.isActive ? 'success' : 'danger'} size="sm">{recipe.isActive ? 'Active' : 'Inactive'}</Badge>
                                            </div>

                                            {/* Key Financial Badges */}
                                            <div className="grid grid-cols-2 gap-2 my-3 p-3 bg-slate-50/80 rounded-2xl border border-slate-100 text-xs">
                                                <div>
                                                    <p className="text-[10px] text-slate-400 font-semibold uppercase">Cost / Serving</p>
                                                    <p className="font-bold text-purple-700 mt-0.5">{recipe.costPerYield ? formatNaira(recipe.costPerYield) : '—'}</p>
                                                </div>
                                                <div>
                                                    <p className="text-[10px] text-slate-400 font-semibold uppercase">Menu Price</p>
                                                    <p className="font-bold text-emerald-700 mt-0.5">{formatNaira(price)}</p>
                                                </div>
                                            </div>

                                            {/* Food cost percentage benchmark */}
                                            {foodCostPct !== null && (
                                                <div className="mb-3">
                                                    <div className="flex items-center justify-between text-xs mb-1">
                                                        <span className="text-slate-400 font-medium">Food Cost Ratio</span>
                                                        <span className={clsx(
                                                            'font-extrabold text-xs',
                                                            foodCostPct <= 32 ? 'text-emerald-700' : foodCostPct <= 45 ? 'text-amber-700' : 'text-rose-700'
                                                        )}>
                                                            {foodCostPct.toFixed(1)}% {foodCostPct <= 32 ? '(Prime)' : foodCostPct <= 45 ? '(Healthy)' : '(High Cost)'}
                                                        </span>
                                                    </div>
                                                    <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                                                        <div
                                                            className={clsx(
                                                                'h-1.5 rounded-full',
                                                                foodCostPct <= 32 ? 'bg-emerald-500' : foodCostPct <= 45 ? 'bg-amber-500' : 'bg-rose-500'
                                                            )}
                                                            style={{ width: `${Math.min(foodCostPct, 100)}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            <div className="space-y-1.5 text-xs text-slate-500 mb-3" onClick={() => navigate(`${routePrefix}/${recipe.id}`)}>
                                                <div className="flex items-center gap-2"><Package size={13} className="text-slate-400" /><span>{recipe.items.length} raw ingredients</span></div>
                                                <div className="flex items-center gap-2"><Layers size={13} className="text-slate-400" /><span>Yields: {recipe.yieldQuantity} {recipe.yieldUomName || 'servings'}</span></div>
                                                {recipe.preparationTime && <div className="flex items-center gap-2"><Timer size={13} className="text-slate-400" /><span>Cook time: {recipe.preparationTime} mins</span></div>}
                                            </div>

                                            {/* Clickable Ingredient chips */}
                                            <div className="flex flex-wrap gap-1 mb-4">
                                                {recipe.items.slice(0, 3).map(item => {
                                                    const ingName = getIngredientName(item);
                                                    const isSelected = selectedIngredient?.toLowerCase() === ingName.toLowerCase();
                                                    return (
                                                        <span
                                                            key={item.id}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedIngredient(isSelected ? null : ingName);
                                                            }}
                                                            className={clsx(
                                                                'px-2 py-0.5 rounded-md text-[10px] font-medium transition-colors cursor-pointer',
                                                                isSelected
                                                                    ? 'bg-amber-500 text-white font-bold'
                                                                    : 'bg-slate-100 hover:bg-amber-100 hover:text-amber-900 text-slate-700'
                                                            )}
                                                            title={`Click to filter by ${ingName}`}
                                                        >
                                                            {ingName}
                                                        </span>
                                                    );
                                                })}
                                                {recipe.items.length > 3 && (
                                                    <span
                                                        onClick={() => navigate(`${routePrefix}/${recipe.id}`)}
                                                        className="px-2 py-0.5 bg-amber-50 text-amber-800 rounded-md text-[10px] font-bold"
                                                    >
                                                        +{recipe.items.length - 3} more
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Card Footer: 1-Click Cook & Management */}
                                        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate('/bom/production');
                                                }}
                                                className={clsx(
                                                    'flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold text-white shadow-xs hover:opacity-95 transition-all',
                                                    isProduction
                                                        ? 'bg-gradient-to-r from-indigo-600 to-teal-600 shadow-indigo-500/20'
                                                        : 'bg-gradient-to-r from-amber-500 to-orange-600 shadow-amber-500/20'
                                                )}
                                            >
                                                {isProduction ? <><Zap size={13} /> Produce Batch</> : <><ChefHat size={13} /> Cook Batch</>}
                                            </button>

                                            <div className="flex items-center gap-1 opacity-80 group-hover:opacity-100 transition-opacity">
                                                <button onClick={(e) => { e.stopPropagation(); navigate(`${routePrefix}/${recipe.id}`); }} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100" title="View Details"><Eye size={14} /></button>
                                                <button onClick={(e) => { e.stopPropagation(); navigate(`${routePrefix}/${recipe.id}/edit`); }} className="p-1.5 rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50" title="Edit Recipe"><Pencil size={14} /></button>
                                                {recipe.isActive && <button onClick={(e) => { e.stopPropagation(); setConfirmDelete(recipe); }} className="p-1.5 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-50" title="Deactivate"><Trash2 size={14} /></button>}
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                )}
                {!isLoading && viewMode === 'card' && filteredRecipes.length > 0 && (
                    <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} pageSize={pageSize} onPageChange={setCurrentPage} onPageSizeChange={setPageSize} />
                )}
            </div>

            {/* Deactivate Modal */}
            <AnimatePresence>
                {confirmDelete && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setConfirmDelete(null)}>
                        <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                            className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6 text-center" onClick={e => e.stopPropagation()}>
                            <div className="w-16 h-16 rounded-2xl bg-rose-100 flex items-center justify-center mx-auto mb-4"><Trash2 size={32} className="text-rose-500" /></div>
                            <h3 className="text-lg font-bold">Deactivate "{confirmDelete.name}"?</h3>
                            <p className="text-sm text-slate-500 mt-2">It can be reactivated later.</p>
                            <div className="flex gap-3 mt-6">
                                <button onClick={() => setConfirmDelete(null)} className="flex-1 px-4 py-2.5 border rounded-xl text-sm font-medium">Cancel</button>
                                <button onClick={() => deactivateMutation.mutate(confirmDelete.id)} className="flex-1 px-4 py-2.5 bg-rose-500 text-white rounded-xl text-sm font-semibold">Deactivate</button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

// Upgraded QuickStat component
function QuickStat({ label, value, icon: Icon, gradient, subtitle }: {
    label: string;
    value: React.ReactNode;
    icon: React.ElementType;
    gradient: string;
    subtitle?: string;
}) {
    return (
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -2 }}
            className="bg-white rounded-2xl border border-slate-200/90 shadow-xs p-4 hover:shadow-md transition-all flex flex-col justify-between">
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider truncate">{label}</p>
                    <p className="text-lg sm:text-2xl font-black text-slate-900 mt-1 tracking-tight truncate">{value}</p>
                </div>
                <div className={`p-2.5 rounded-xl bg-gradient-to-br ${gradient} shadow-md shrink-0 text-white`}>
                    <Icon className="w-4 h-4" />
                </div>
            </div>
            {subtitle && (
                <p className="text-[11px] font-medium text-slate-500 mt-2 truncate">{subtitle}</p>
            )}
        </motion.div>
    );
}